import React, { useEffect } from 'react';
import { FaTimes, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(11, 19, 32, 0.85);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const Modal = styled.div`
  background: var(--panel-bg, #0f172a);
  border: 1px solid var(--border-soft, #1e293b);
  border-radius: 12px;
  max-width: 460px;
  width: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-soft, #1e293b);
  background: var(--header-bg, #0b1320);
  border-radius: 12px 12px 0 0;
`;

const Title = styled.h3`
  margin: 0;
  color: var(--text-primary, #f8fafc);
  font-size: 1.05rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${(p) => p.$danger && `color: var(--alert-error, #ef4444);`}
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary, #94a3b8);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s;

  &:hover {
    background: var(--border-soft, #1e293b);
    color: var(--text-primary, #f8fafc);
  }
`;

const Content = styled.div`
  padding: 1.25rem 1.5rem;
  color: var(--text-secondary, #cbd5e1);
  font-size: 0.9rem;
  line-height: 1.5;
  white-space: pre-line;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border-soft, #1e293b);
  background: var(--header-bg, #0b1320);
  border-radius: 0 0 12px 12px;
`;

const Button = styled.button`
  padding: 0.6rem 1.25rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${(props) => props.$primary ? `
    background: ${props.$danger ? 'var(--alert-error, #ef4444)' : 'var(--accent-primary, #3b82f6)'};
    color: white;
    border: none;

    &:hover:not(:disabled) { filter: brightness(1.1); }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
  ` : `
    background: transparent;
    color: var(--text-secondary, #94a3b8);
    border: 1px solid var(--border-soft, #1e293b);

    &:hover { background: var(--border-soft, #1e293b); color: var(--text-primary, #f8fafc); }
  `}
`;

/**
 * Modal de confirmación genérico (reemplaza window.confirm).
 * Props:
 *  - isOpen: bool
 *  - title, message: textos
 *  - confirmLabel, cancelLabel: textos de los botones
 *  - danger: estilo destructivo (rojo)
 *  - isLoading: deshabilita el botón de confirmar
 *  - onConfirm, onCancel: callbacks
 */
const ConfirmModal = ({
  isOpen,
  title = 'Confirmar',
  message = '',
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  danger = false,
  isLoading = false,
  onConfirm,
  onCancel,
}) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel?.();
      else if (e.key === 'Enter' && !isLoading) onConfirm?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, isLoading, onConfirm, onCancel]);

  if (!isOpen) return null;

  return (
    <Overlay onClick={onCancel}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title $danger={danger}>
            {danger && <FaExclamationTriangle />} {title}
          </Title>
          <CloseButton onClick={onCancel} aria-label="Cerrar">
            <FaTimes />
          </CloseButton>
        </Header>

        <Content>{message}</Content>

        <Footer>
          <Button type="button" onClick={onCancel}>
            <FaTimes /> {cancelLabel}
          </Button>
          <Button type="button" $primary $danger={danger} onClick={onConfirm} disabled={isLoading}>
            {isLoading ? 'Procesando…' : (<><FaCheck /> {confirmLabel}</>)}
          </Button>
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default ConfirmModal;
